<template>
  <Button
    :class="computedClass"
    :type="type"
    :size="size"
    :variant="variant"
    :loading="loading"
    :disabled="disabled || null"
    :aria-disabled="disabled"
    :aria-busy="loading"
    @click.prevent="submitStep"
  >
    {{ value }}
  </Button>
</template>

<script lang="ts">
import { PropType } from 'vue';
import Button, { ButtonSize, ButtonType, ButtonVariant } from '@/components/button/Button.vue';
import { emitRegisterNextButton } from '@/helpers/routeStepperNextButtonBus';

interface Errors {
  [key: string]: any;
}

export default {
  name: 'RouteStepperNextButton',
  components: { Button },
  inject: {
    navigateToNextStep: {
      default: () => {},
    },
  },
  inheritAttrs: false,
  props: {
    stepData: {
      type: Object,
      default: () => ({}),
    },
    draftData: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String as PropType<ButtonVariant>,
      default: 'primary',
    },
    type: {
      type: String as PropType<ButtonType>,
      default: 'submit',
    },
    value: {
      type: String,
      default: 'Next',
    },
    size: {
      type: String as PropType<ButtonSize>,
      default: 'medium',
    },
    validate: {
      type: Function,
      default: () => ({}),
    },
  },
  computed: {
    computedClass() {
      return this.$attrs.class || 'w-full md:max-w-max';
    },
    injectedNavigateToNextStep(): Function {
      // this is a fallback to make testing easier, otherwise we would need to mock the injected function for every test
      return this.navigateToNextStep || (() => {});
    },
  },
  mounted() {
    this.registerWithStepper();
  },
  activated() {
    this.registerWithStepper();
  },
  methods: {
    registerWithStepper(): void {
      emitRegisterNextButton(this);
    },
    async submitStep({ emitErrors = true }: { emitErrors?: boolean } = {}): Promise<boolean> {
      const result = await this.validate();

      // this ensures the validate function returns an errors object
      if (typeof result !== 'object' || result === null || Array.isArray(result)) {
        console.warn('RouteStepperNextButton: validate function must return an errors object.');
        return false;
      }

      const errors: Errors = result;
      const hasErrors: boolean = Object.keys(errors).length > 0;
      if (emitErrors) {
        this.$emit('errors', errors);
      }

      if (!hasErrors) {
        await this.injectedNavigateToNextStep(this.stepData, this.draftData);
      }

      return !hasErrors;
    },
  },
};
</script>
